import * as Constants from "Data/Constants";
import { Power4, Sine } from "gsap/EasePack";
import { TimelineLite } from "gsap/TimelineLite";
import { TweenLite } from "gsap/TweenLite";
import * as HLF from "html-living-framework";

export class KeyboardFocus extends PIXI.Container implements HLF.Input.IKeyboardFocus {
    private readonly _corners: PIXI.Sprite[];
    private _tweenAlpha: TweenLite | undefined;
    private _tweenLoop: TimelineLite | undefined;
    private _animPerc: number;
    private _target: HLF.Input.IFocusable | undefined;

    constructor() {
        super();
        this._animPerc = 0;

        this._corners = new Array<PIXI.Sprite>(4);
        for (let i = 0; i < this._corners.length; ++i) {
            this._corners[i] = HLF.PixiUtils.makeSprite("keyboard-focus", Constants.SPRITESHEET_UI);
            this._corners[i].rotation = i * Math.PI * 0.5;
            this.addChild(this._corners[i]);
        }
        this.hide(undefined, true);
    }

    public get target(): HLF.Input.IFocusable | undefined {
        return this._target;
    }

    public show(pFocusable: HLF.Input.IFocusable): void {
        this.killTweens();

        this._target = pFocusable;
        this.redraw();

        this._tweenAlpha = TweenLite.to(this, 0.25, {
            alpha: 1,
            ease: Sine.easeInOut,
        });
        this._animPerc = 0;

        this._tweenLoop = new TimelineLite({
            repeat: -1,
            onUpdate: this.updateCorners.bind(this),
        });
        this._tweenLoop.to(this, 0.5, {
            animPerc: 1,
            ease: Power4.easeOut,
        });
        this._tweenLoop.to(this, 0.5, {
            animPerc: 0,
            ease: Power4.easeIn,
        });
    }

    public hide(pOnComplete?: () => void, pInstantly: boolean = false): void {
        this.killTweens();

        this._target = undefined;

        this._tweenAlpha = TweenLite.to(this, pInstantly ? 0 : 0.1, {
            alpha: 0,
            ease: Sine.easeInOut,
            onComplete: pOnComplete,
        });
    }

    public redraw(): void {
        requestAnimationFrame(() => {
            if (this._target !== undefined) {
                this.position.copy(this.parent.worldTransform.applyInverse(this._target.getFocusPosition()));
                const size: PIXI.Point = this.parent.worldTransform.applyInverse(this._target.getFocusSize());
                for (let i = 0; i < this._corners.length; ++i) {
                    this._corners[i].x = size.x * 0.5 * ((i === 1 || i === 2) ? 1 : -1);
                    this._corners[i].y = size.y * 0.5 * ((Math.floor(i / 2) === 0) ? -1 : 1);
                }
            }
        });
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean): void {
        this.killTweens();
        super.destroy(pOptions);
    }

    private killTweens(): void {
        if (this._tweenLoop !== undefined) {
            this._tweenLoop.kill();
        }
        if (this._tweenAlpha !== undefined) {
            this._tweenAlpha.kill();
        }
    }

    private get animPerc(): number {
        return this._animPerc;
    }

    private set animPerc(pValue: number) {
        this._animPerc = pValue;
    }

    private updateCorners(): void {
        for (const corner of this._corners) {
            corner.anchor.set(0.5 + 0.15 * this._animPerc);
        }
    }
}
